import React from "react";
import { Link } from "react-router-dom";
import {
  Database, ShieldCheck, Briefcase, Container, Stamp, Calculator,
  Users, BarChart3, ArrowRight, CheckCircle2, Sparkles, FileText,
  Globe, Lock, ChevronRight, Building2, Wine, Truck, FileCheck,
  TrendingUp, AlertCircle, Brain, Workflow, Download,
} from "lucide-react";

const HERO_BG = "https://images.pexels.com/photos/9219074/pexels-photo-9219074.jpeg";
const PDF_URL = `${process.env.REACT_APP_BACKEND_URL}/api/gestionale/pitch.pdf`;

const MODULES = [
  {
    n: "01", icon: Building2, title: "Supplier Onboarding",
    sub: "Attivare nuovi fornitori europei",
    status: "roadmap",
    points: [
      "Anagrafica fornitore: ragione sociale, P. IVA, paese, referenti commerciali",
      "Contratti: esclusiva territoriale, condizioni commerciali, MOQ, Incoterm, metodo pagamento",
      "Repository documenti: cataloghi, certificazioni, schede tecniche, certificati export",
    ],
    kpi: ["Tempo onboarding", "Documentazione completa", "Fornitori attivi"],
  },
  {
    n: "02", icon: Wine, title: "Product Compliance Hub",
    sub: "Centralizzare tutti gli SKU idonei per il Brasile",
    status: "partial",
    statusLabel: "AI Scanner LIVE",
    points: [
      "Product Master: SKU, Brand, categoria, origine, packaging, shelf life",
      "Regulatory Data: MAPA category, NCM code, alcohol %, volume, ingredienti, allergeni",
      "Document Repository: Technical Sheet · COA · Certificate of Origin · Label artwork",
      "Workflow: NEW → DOCUMENTS PENDING → UNDER REVIEW → LAB TEST → APPROVED → COMMERCIAL ACTIVE",
    ],
    kpi: ["SKU approvati", "SKU in revisione", "Tempo approvazione"],
  },
  {
    n: "03", icon: Calculator, title: "Quotation & Deal Desk",
    sub: "Calcolare prezzi, costi e margini",
    status: "roadmap",
    points: [
      "Cost Simulation: EXW · FOB · Freight · Insurance · Customs · Broker fees",
      "Margin Calculator: prezzo vendita − costo fornitore = margine",
      "Quotation Management: preventivi, revisioni, storico offerte",
    ],
    kpi: ["Margine per SKU", "Margine per cliente", "Tasso conversione offerte"],
  },
  {
    n: "04", icon: Container, title: "Container & Shipping",
    sub: "Consolidare merce da più fornitori",
    status: "roadmap",
    points: [
      "Container Planning: CBM, peso, volume, temperatura",
      "Consolidation: multi-fornitore in container unico",
      "Shipping Tracking: booking, ETD, ETA, vessel tracking",
      "Export Documents: Commercial Invoice, Packing List, BL, COO, COA",
    ],
    kpi: ["Fill rate", "Freight cost / unit", "Transit time"],
  },
  {
    n: "05", icon: Stamp, title: "MAPA Workflow",
    sub: "Gestire compliance importazione Brasile",
    status: "roadmap",
    points: [
      "Dossier Import: Invoice, Packing List, COA, Labels, Certificates",
      "LI / SISCOMEX: preparazione pratica, invio broker, tracking approvazione",
      "Workflow: SHIPMENT CREATED → DOCUMENT PACK READY → BROKER REVIEW → LI SUBMITTED → MAPA ANALYSIS → APPROVED",
      "Alert: documenti mancanti · pratiche bloccate · exigências",
    ],
    kpi: ["Giorni approvazione", "Pratiche aperte", "Pratiche chiuse"],
  },
  {
    n: "06", icon: TrendingUp, title: "Commission Engine",
    sub: "Calcolare commissioni",
    status: "roadmap",
    points: [
      "Formula: Commissione = FOB × % oppure Margine = Sell Price − Cost",
      "Report per fornitore · per cliente · per container · per trimestre",
    ],
    kpi: ["Commissioni mensili", "Margine totale", "Top clienti"],
  },
  {
    n: "07", icon: Users, title: "CRM B2B Brazil",
    sub: "Gestire clienti brasiliani",
    status: "roadmap",
    points: [
      "Tipologie: Importatori · Distributori · Horeca · Retail",
      "Pipeline: LEAD → SAMPLE → QUOTATION → NEGOTIATION → PO → SHIPMENT → REORDER",
      "Funzioni: Follow-up, Meeting, Campionature, Storico ordini",
    ],
    kpi: ["Conversion rate", "Clienti attivi", "Riordini"],
  },
  {
    n: "08", icon: BarChart3, title: "Management Dashboard",
    sub: "Cockpit unico operations + finance",
    status: "partial",
    statusLabel: "Base LIVE",
    points: [
      "Procurement: fornitori attivi · lead time",
      "Compliance: SKU approvati · tempo MAPA",
      "Logistics: container in transito · costi logistici",
      "Sales: fatturato · margini · clienti attivi",
      "Finance: commissioni · cash flow · exposure EUR/BRL",
    ],
    kpi: ["KPI cross-funzionali real-time"],
  },
];

const ARCHITECTURE_NODES = [
  { label: "Fornitori Europa", icon: Building2 },
  { label: "Supplier Portal", icon: Database },
  { label: "Product Compliance Hub", icon: ShieldCheck },
  { label: "Quotation Engine", icon: Calculator },
  { label: "Shipping Engine", icon: Container },
  { label: "MAPA Workflow", icon: Stamp },
  { label: "Importatore Brasile", icon: Truck },
  { label: "Clienti B2B", icon: Users },
];

const STATUS_BADGE = {
  live: { bg: "bg-emerald-500/15", text: "text-emerald-300", border: "border-emerald-500/30", label: "LIVE" },
  partial: { bg: "bg-[#C9A961]/15", text: "text-[#C9A961]", border: "border-[#C9A961]/40", label: "IN PROGRESS" },
  roadmap: { bg: "bg-white/5", text: "text-white/50", border: "border-white/15", label: "ROADMAP" },
};

function StatusPill({ status, label }) {
  const cfg = STATUS_BADGE[status] || STATUS_BADGE.roadmap;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-[0.25em] font-bold rounded-sm border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
      {status === "live" && <CheckCircle2 className="h-3 w-3" />}
      {status === "partial" && <Sparkles className="h-3 w-3" />}
      {status === "roadmap" && <ChevronRight className="h-3 w-3" />}
      {label || cfg.label}
    </span>
  );
}

export default function GestionalePitch() {
  return (
    <div data-testid="gestionale-pitch-page" className="min-h-screen bg-[#0a0a0a] text-white">
      {/* ============== HERO ============== */}
      <section className="relative overflow-hidden border-b border-[#C9A961]/15">
        <img src={HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a0a0a] via-[#0a0a0a]/80 to-[#1a0608]/60" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-20 md:py-28">
          <div className="flex items-center gap-3 mb-10">
            <div className="h-10 w-10 border border-[#C9A961] flex items-center justify-center">
              <span className="editorial-heading text-[#C9A961] text-xl">A</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#C9A961] font-bold">Amazonico 3000 · Pitch Gestionale</div>
          </div>

          <div className="text-xs uppercase tracking-[0.4em] text-[#C9A961] mb-6 flex items-center gap-3">
            <div className="h-px w-10 bg-[#C9A961]" /> B2B Operations Suite · Italia → Brasile
          </div>
          <h1 className="editorial-heading text-5xl md:text-7xl lg:text-8xl leading-[0.95] font-light max-w-5xl">
            Una società di<br /><span className="italic text-[#C9A961]">intermediazione</span><br />governata da software.
          </h1>
          <p className="mt-10 text-white/70 text-lg md:text-xl leading-relaxed max-w-3xl">
            Coordiniamo fornitori europei, compliance MAPA, importatore brasiliano e distribuzione B2B
            <span className="text-white"> senza produrre, senza stoccare. </span>
            Il valore aggiunto è la <span className="text-[#C9A961]">selezione</span>, la <span className="text-[#C9A961]">compliance</span>,
            il <span className="text-[#C9A961]">consolidamento logistico</span> e il <span className="text-[#C9A961]">coordinamento commerciale</span> —
            tutto in un'unica console operativa.
          </p>

          <div className="mt-12 flex flex-wrap gap-3">
            <Link to="/gestionale" data-testid="hero-cta-access" className="inline-flex items-center gap-3 px-7 py-4 bg-[#C9A961] text-black font-bold uppercase tracking-[0.2em] text-xs hover:bg-white transition-colors">
              <Lock className="h-4 w-4" /> Accedi al gestionale
            </Link>
            <a
              href={PDF_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-pdf"
              className="inline-flex items-center gap-3 px-7 py-4 border border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961] hover:text-black uppercase tracking-[0.2em] text-xs font-bold transition-colors"
            >
              <Download className="h-4 w-4" /> Scarica PDF
            </a>
            <a href="#moduli" data-testid="hero-cta-modules" className="inline-flex items-center gap-3 px-7 py-4 border border-white/20 text-white hover:bg-white/5 uppercase tracking-[0.2em] text-xs font-bold">
              I 8 moduli <ArrowRight className="h-4 w-4" />
            </a>
          </div>

          {/* Stat strip */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-y-8 gap-x-6 max-w-4xl border-t border-[#C9A961]/15 pt-10">
            {[
              { v: "0", l: "Stock immobilizzato" },
              { v: "8", l: "Moduli operativi" },
              { v: "21", l: "Campi MAPA estratti via AI" },
              { v: "EUR↔BRL", l: "Exposure tracking" },
            ].map((s, i) => (
              <div key={i}>
                <div className="editorial-heading text-3xl md:text-4xl text-[#C9A961]">{s.v}</div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mt-2">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== BUSINESS MODEL ============== */}
      <section className="border-b border-white/5 py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-[280px_1fr] gap-10">
            <div>
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#C9A961] font-bold mb-3">Business Model</div>
              <h2 className="editorial-heading text-4xl md:text-5xl leading-tight">
                Asset-light.<br />
                <span className="italic text-[#C9A961]">Margin-heavy.</span>
              </h2>
            </div>
            <div className="space-y-6 text-white/75 text-base leading-relaxed">
              <p>
                Non produciamo, non gestiamo stock in Europa. Coordiniamo una filiera che parte dal vigneto italiano e
                approda al palato brasiliano, e per ogni passaggio ci facciamo carico della parte che il fornitore non sa
                (o non vuole) gestire: <span className="text-white">documenti, dogana, dossier MAPA, consolidamento container, relazione con l'importatore</span>.
              </p>
              <div className="grid sm:grid-cols-2 gap-4 pt-4">
                {[
                  ["Selezione fornitori", Building2],
                  ["Compliance Brasile", ShieldCheck],
                  ["Consolidamento logistico", Container],
                  ["Gestione documentale", FileCheck],
                  ["Coordinamento importazione", Stamp],
                  ["Sviluppo commerciale B2B", Briefcase],
                ].map(([label, Icon], i) => (
                  <div key={i} className="flex items-center gap-3 border-l-2 border-[#C9A961]/40 pl-4 py-1.5">
                    <Icon className="h-4 w-4 text-[#C9A961] shrink-0" />
                    <span className="text-sm uppercase tracking-wider font-semibold text-white">{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== ARCHITECTURE FLOW ============== */}
      <section className="border-b border-white/5 py-20 md:py-24 bg-gradient-to-b from-transparent to-[#1a0608]/20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#C9A961] font-bold mb-3">Architettura Software</div>
            <h2 className="editorial-heading text-4xl md:text-5xl leading-tight">Una pipeline, otto stadi.</h2>
            <p className="mt-4 text-white/60 max-w-2xl mx-auto">
              Dal fornitore italiano al cliente brasiliano, ogni passaggio è tracciato, documentato, automatizzato.
            </p>
          </div>

          <div className="flex flex-wrap items-stretch justify-center gap-3 md:gap-4">
            {ARCHITECTURE_NODES.map((node, i) => (
              <React.Fragment key={i}>
                <div className="flex flex-col items-center justify-center gap-2 px-4 md:px-5 py-4 bg-black/40 border border-[#C9A961]/25 rounded-sm min-w-[140px] flex-1 sm:flex-none hover:border-[#C9A961] hover:bg-[#C9A961]/5 transition-colors group">
                  <node.icon className="h-5 w-5 text-[#C9A961] group-hover:scale-110 transition-transform" />
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-center text-white/85">{node.label}</div>
                </div>
                {i < ARCHITECTURE_NODES.length - 1 && (
                  <div className="hidden lg:flex items-center text-[#C9A961]/40">
                    <ChevronRight className="h-4 w-4" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ============== AI HIGHLIGHT ============== */}
      <section className="border-b border-white/5 py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-[#C9A961] font-bold mb-4">
                <Brain className="h-3.5 w-3.5" /> AI Differenziatore
              </div>
              <h2 className="editorial-heading text-4xl md:text-5xl leading-tight">
                <span className="italic text-[#C9A961]">21 campi MAPA</span><br />
                estratti in 3 secondi.
              </h2>
              <p className="mt-6 text-white/70 leading-relaxed">
                Carichi una scheda tecnica PDF di un vino italiano. GPT-5.2 legge, capisce, estrae:
                alcool, volume, ingredienti, allergeni, NCM code, MAPA category, lot number, bottling date.
                Il dossier compliance per il Brasile si compone <span className="text-white">solo</span>.
              </p>
              <div className="mt-8 grid grid-cols-3 gap-4">
                <div>
                  <div className="editorial-heading text-3xl text-[#C9A961]">99%</div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 mt-1">Confidence media</div>
                </div>
                <div>
                  <div className="editorial-heading text-3xl text-[#C9A961]">~3s</div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 mt-1">Per documento</div>
                </div>
                <div>
                  <div className="editorial-heading text-3xl text-[#C9A961]">$0.001</div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 mt-1">Costo per scan</div>
                </div>
              </div>
            </div>
            <div className="bg-black/60 border border-[#C9A961]/25 rounded-sm p-6 font-mono text-[11px] leading-relaxed">
              <div className="flex items-center gap-2 pb-3 mb-3 border-b border-white/10">
                <div className="flex gap-1">
                  <div className="w-2 h-2 rounded-full bg-red-500/60" />
                  <div className="w-2 h-2 rounded-full bg-yellow-500/60" />
                  <div className="w-2 h-2 rounded-full bg-green-500/60" />
                </div>
                <div className="text-white/40 text-[10px] ml-2">scan-result.json</div>
              </div>
              <pre className="text-white/80 overflow-x-auto"><code>{`{
  "brand": "Castello Banfi",
  "wine_name": "Brunello di Montalcino DOCG",
  "vintage": 2018,
  "country_origin": "Italy",
  "region": "Tuscany - Montalcino",
  "grape_varieties": ["Sangiovese"],
  "alcohol_pct": 14.5,
  "volume_ml": 750,
  "ph": 3.45,
  "residual_sugar_g_per_l": 1.8,
  "total_acidity_g_per_l": 5.7,
  "ingredients": ["Grapes", "sulphites"],
  "allergens": ["sulphites"],
  "ncm_code": "2204.21.00",
  "mapa_category": "Vinho fino tinto seco",
  "lot_number": "BRU2018-LOT-A12",
  "bottling_date": "2022-03-15",
  "confidence": 0.99
}`}</code></pre>
            </div>
          </div>
        </div>
      </section>

      {/* ============== 8 MODULI ============== */}
      <section id="moduli" className="border-b border-white/5 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#C9A961] font-bold mb-3">Le 8 funzioni</div>
            <h2 className="editorial-heading text-4xl md:text-6xl leading-tight">
              Otto moduli.<br />
              <span className="italic text-[#C9A961]">Una console.</span>
            </h2>
            <div className="mt-8 flex justify-center gap-3 flex-wrap">
              <StatusPill status="live" />
              <StatusPill status="partial" />
              <StatusPill status="roadmap" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {MODULES.map((m, i) => (
              <article key={i} data-testid={`module-${m.n}`} className="bg-black/40 border border-white/10 rounded-sm p-6 lg:p-8 hover:border-[#C9A961]/40 transition-colors group">
                <div className="flex items-start justify-between gap-4 mb-5">
                  <div className="flex items-start gap-4">
                    <div className="text-[#C9A961]/30 editorial-heading text-4xl leading-none font-light shrink-0 group-hover:text-[#C9A961] transition-colors">{m.n}</div>
                    <div>
                      <h3 className="editorial-heading text-2xl text-white">{m.title}</h3>
                      <p className="text-sm text-white/50 mt-1">{m.sub}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <m.icon className="h-5 w-5 text-[#C9A961]" />
                    <StatusPill status={m.status} label={m.statusLabel} />
                  </div>
                </div>
                <ul className="space-y-2.5 mb-5">
                  {m.points.map((p, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm text-white/75 leading-relaxed">
                      <ChevronRight className="h-3.5 w-3.5 text-[#C9A961]/70 mt-1 shrink-0" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
                <div className="pt-4 border-t border-white/10">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 font-bold mb-2 flex items-center gap-1.5">
                    <BarChart3 className="h-3 w-3" /> KPI
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {m.kpi.map((k, j) => (
                      <span key={j} className="text-[10px] uppercase tracking-wider px-2 py-1 bg-white/5 border border-white/10 text-white/70">
                        {k}
                      </span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ============== ROADMAP ============== */}
      <section className="border-b border-white/5 py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#C9A961] font-bold mb-3">Status & Roadmap</div>
          <h2 className="editorial-heading text-4xl md:text-5xl leading-tight mb-12">Cosa è già operativo.</h2>

          <div className="grid md:grid-cols-3 gap-5">
            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-emerald-300">Live oggi</div>
              </div>
              <ul className="space-y-3 text-sm text-white/80 leading-relaxed">
                <li>✓ Catalog Imports CSV (vini, lotti)</li>
                <li>✓ PDF Document Repository (5 doc_type)</li>
                <li>✓ AI Scanner GPT-5.2 (21 campi MAPA)</li>
                <li>✓ Auto-mirror su scheda vino</li>
                <li>✓ Storico job + audit trail</li>
                <li>✓ Dashboard KPI base</li>
              </ul>
            </div>
            <div className="bg-[#C9A961]/5 border border-[#C9A961]/30 rounded-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="h-5 w-5 text-[#C9A961]" />
                <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#C9A961]">Prossimi sprint</div>
              </div>
              <ul className="space-y-3 text-sm text-white/80 leading-relaxed">
                <li>→ UI Compliance Hub (cockpit SKU)</li>
                <li>→ Workflow approval con semafori</li>
                <li>→ OCR fallback per PDF scannerizzati</li>
                <li>→ Quotation & Deal Desk (cost sim)</li>
                <li>→ Container consolidation planner</li>
              </ul>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <Workflow className="h-5 w-5 text-white/60" />
                <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/60">Roadmap 2026</div>
              </div>
              <ul className="space-y-3 text-sm text-white/70 leading-relaxed">
                <li>○ Supplier Onboarding completo</li>
                <li>○ MAPA LI/SISCOMEX integration</li>
                <li>○ Commission engine + report</li>
                <li>○ CRM B2B Brazil pipeline</li>
                <li>○ Management Dashboard finance</li>
                <li>○ Hedging EUR↔BRL exposure</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ============== FINAL CTA ============== */}
      <section className="relative overflow-hidden py-24 md:py-32">
        <img src={HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/80 via-[#0a0a0a] to-[#1a0608]" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#C9A961] font-bold mb-5 flex items-center justify-center gap-3">
            <div className="h-px w-10 bg-[#C9A961]" /> Demo & Onboarding <div className="h-px w-10 bg-[#C9A961]" />
          </div>
          <h2 className="editorial-heading text-4xl md:text-6xl leading-tight">
            Pronto a vedere<br />
            <span className="italic text-[#C9A961]">il gestionale in azione?</span>
          </h2>
          <p className="mt-6 text-white/60 text-lg max-w-2xl mx-auto leading-relaxed">
            Accedi con credenziali demo, esplora i moduli operativi, prova il scanner AI sui tuoi PDF.
          </p>
          <div className="mt-12 flex flex-wrap gap-4 justify-center">
            <Link to="/gestionale" data-testid="final-cta-access" className="inline-flex items-center gap-3 px-8 py-4 bg-[#C9A961] text-black font-bold uppercase tracking-[0.2em] text-xs hover:bg-white transition-colors">
              <Lock className="h-4 w-4" /> Accedi al gestionale <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href={PDF_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="final-cta-pdf"
              className="inline-flex items-center gap-3 px-8 py-4 border border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961] hover:text-black uppercase tracking-[0.2em] text-xs font-bold transition-colors"
            >
              <Download className="h-4 w-4" /> Scarica pitch PDF
            </a>
            <Link to="/" data-testid="final-cta-home" className="inline-flex items-center gap-3 px-8 py-4 border border-white/20 text-white hover:bg-white/5 uppercase tracking-[0.2em] text-xs font-bold">
              Torna ad Amazonico 3000
            </Link>
          </div>
          <div className="mt-16 flex items-center justify-center gap-6 text-[10px] uppercase tracking-[0.3em] text-white/30">
            <span className="flex items-center gap-1.5"><Globe className="h-3 w-3" /> Italia → Brasile</span>
            <span className="h-px w-6 bg-white/20" />
            <span className="flex items-center gap-1.5"><Lock className="h-3 w-3" /> Accesso riservato</span>
            <span className="h-px w-6 bg-white/20" />
            <span className="flex items-center gap-1.5"><FileText className="h-3 w-3" /> Audit trail</span>
          </div>
        </div>
      </section>
    </div>
  );
}
